/**
 * Centralized language configuration
 * Single source of truth for all language settings
 */

// Language configuration interface
export interface LanguageConfig {
  code: string;
  name: {
    native: string;
    english: string;
  };
  isRtl?: boolean; // Optional, defaults to false
}

// All supported languages configuration
export const LANGUAGES: Record<string, LanguageConfig> = {
  en: {
    code: "en",
    name: { native: "English", english: "English" },
  },
  ru: {
    code: "ru",
    name: { native: "Русский", english: "Russian" },
  },
  // Enabled languages
  id: {
    code: "id",
    name: { native: "Bahasa Indonesia", english: "Indonesian" },
  },
  ar: {
    code: "ar",
    name: { native: "العربية", english: "Arabic" },
    isRtl: true,
  },
  fa: {
    code: "fa",
    name: { native: "فارسی", english: "Persian" },
    isRtl: true,
  },
  pt: {
    code: "pt",
    name: { native: "Português", english: "Portuguese" },
  },
  es: {
    code: "es",
    name: { native: "Español", english: "Spanish" },
  },
  tr: {
    code: "tr",
    name: { native: "Türkçe", english: "Turkish" },
  },
  de: {
    code: "de",
    name: { native: "Deutsch", english: "German" },
  },
  fr: {
    code: "fr",
    name: { native: "Français", english: "French" },
  },
  uk: {
    code: "uk",
    name: { native: "Українська", english: "Ukrainian" },
  },
  uz: {
    code: "uz",
    name: { native: "Oʻzbek", english: "Uzbek" },
  },
  // Disabled languages (kept for future use)
  // in: { code: "in", name: { native: "हिन्दी", english: "Hindi" } },
  // zh: { code: "zh", name: { native: "中文", english: "Chinese" } },
  // ja: { code: "ja", name: { native: "日本語", english: "Japanese" } },
  // ko: { code: "ko", name: { native: "한국어", english: "Korean" } },
  // it: { code: "it", name: { native: "Italiano", english: "Italian" } },
  // ca: { code: "ca", name: { native: "Català", english: "Catalan" } },
  // hr: { code: "hr", name: { native: "Hrvatski", english: "Croatian" } },
  // cs: { code: "cs", name: { native: "Čeština", english: "Czech" } },
  // nl: { code: "nl", name: { native: "Nederlands", english: "Dutch" } },
  // fi: { code: "fi", name: { native: "Suomi", english: "Finnish" } },
  // hu: { code: "hu", name: { native: "Magyar", english: "Hungarian" } },
  // kk: { code: "kk", name: { native: "Қазақ", english: "Kazakh" } },
  // ms: { code: "ms", name: { native: "Bahasa Melayu", english: "Malay" } },
  // no: { code: "no", name: { native: "Norsk", english: "Norwegian" } },
  // pl: { code: "pl", name: { native: "Polski", english: "Polish" } },
  // sr: { code: "sr", name: { native: "Српски", english: "Serbian" } },
  // sk: { code: "sk", name: { native: "Slovenčina", english: "Slovak" } },
  // sv: { code: "sv", name: { native: "Svenska", english: "Swedish" } },
  // ha: { code: "ha", name: { native: "Hausa", english: "Hausa" } },
  // yo: { code: "yo", name: { native: "Yorùbá", english: "Yoruba" } },
  // jv: { code: "jv", name: { native: "ꦧꦱꦗꦮ", english: "Javanese" } },
  // mr: { code: "mr", name: { native: "मराठी", english: "Marathi" } },
  // te: { code: "te", name: { native: "తెలుగు", english: "Telugu" } },
  // vi: { code: "vi", name: { native: "Tiếng Việt", english: "Vietnamese" } },
  // he: { code: "he", name: { native: "עברית", english: "Hebrew" }, isRtl: true },
} as const;

// Derived configurations
export const supportedLanguages = Object.keys(LANGUAGES) as Array<
  keyof typeof LANGUAGES
>;
export const fallbackLanguage = "en" as const;

// Helper functions
export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return LANGUAGES[code as keyof typeof LANGUAGES];
}

export function isSupportedLanguage(
  code: string
): code is keyof typeof LANGUAGES {
  return code in LANGUAGES;
}

export function getLanguageDisplayName(code: string): {
  native: string;
  english: string;
} {
  const config = getLanguageConfig(code);
  return config?.name || { native: code, english: code };
}

export function isRightToLeft(code: string): boolean {
  const config = getLanguageConfig(code);
  return config?.isRtl || false;
}

export function getAvailableLanguages(): LanguageConfig[] {
  return Object.values(LANGUAGES).sort((a, b) =>
    a.name.english.localeCompare(b.name.english)
  );
}

// Type exports
export type SupportedLanguage = keyof typeof LANGUAGES;
